<template>
  <div>
    <EntitySearchPopover
      :allowSearchScopeChange="false"
      :disabled="disabled"
      :items="totalRoutines"
      :loading="isLoading"
      :matchingItems="totalRoutines"
      :open="openMenu"
      :resultsHeaderText="$t('routines.routine_other')"
      :selectedItems="selectedRoutines"
      :showAdvancedOption="true"
      @clearAll="onClearAll"
      @openChange="openMenu = $event"
      @remove="onRemove"
      @search="getRoutines"
      @select="handleSelectSingleRoutine"
      @showAdvancedSearch="handleOpenAdvanceModal"
    >
      <template v-slot:default>
        <slot name="activator" :selectedRoutines="selectedRoutines" />
      </template>

      <template v-slot:prepend>
        <v-select
          append-icon="mdi-chevron-down"
          class="routine-selector-top-filter"
          hide-details
          item-text="name"
          item-value="value"
          outlined
          :items="selectOptions"
          :menu-props="{
            offsetY: true,
            bottom: true,
          }"
          :value="routineFilterOption"
          @change="handleRoutineSelectChange"
        >
          <template v-slot:selection="{ item }">
            <div class="d-flex">
              <span
                class="subtitle-2 font-weight-medium blue-grey--text text--darken-2"
                >{{ item.name }}
              </span>
            </div>
          </template>
        </v-select>
      </template>

      <template v-slot:item="{ item, searchTerm }">
        <RoutineListItem
          class="pl-1 pr-3 py-0"
          :routine="item"
          :selected="false"
          :searchTerm="searchTerm"
          @click="handleSelectSingleRoutine(item)"
        />
      </template>

      <template v-slot:selectedItem="{ item, searchTerm }">
        <RoutineListItem
          class="pl-1 pr-3 py-0"
          :routine="item"
          :selected="true"
          :searchTerm="searchTerm"
          @click="onRemove(item)"
        />
      </template>
    </EntitySearchPopover>

    <AdvancedRoutineSelector
      v-if="isAdvancedRoutineSelectorShown"
      :routineDefinitionPresetFilter="routineFilterOption"
      :show="isAdvancedRoutineSelectorShown"
      :selectedRoutinesData="selectedRoutines"
      :selectedRoutineIds="selectedRoutineIds"
      @selectRoutines="handleSelectMultipleRoutines"
      @close="isAdvancedRoutineSelectorShown = false"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import getEntityId from '@/lib/getEntityId'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import EntitySearchPopover from '@/components/EntitySearchPopover.vue'
import RoutineListItem from './RoutineListItem.vue'

const IS_EXECUTABLE_BY_ME = 'executableByMe'
const CREATED_BY_ME = 'createdByMe'
const COLLABORATOR = 'collaborator'

export default {
  name: 'RoutineSelector',

  mixins: [mixin, routineDefinitionPermissions],

  components: {
    EntitySearchPopover,
    RoutineListItem,
    AdvancedRoutineSelector: () => import('./AdvancedRoutineSelector.vue'), // Imported this way to prevent cyclic dependencies: RoutineSelector -> AdvancedRoutineSelector -> Filters -> RoutineSelector
  },

  props: {
    disabled: Boolean,
    selectedRoutineIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      cachedRoutines: {},
      isAdvancedRoutineSelectorShown: false,
      isLoading: true,
      openMenu: false,
      routineFilterOption: 'allRoutines',
      routines: [],
      searchTerms: '',
      showAdvancedModal: false,
    }
  },

  mounted() {
    this.getRoutines()
  },

  watch: {
    missingRoutines: {
      immediate: false,
      deep: false,
      handler(missingRoutines) {
        if (missingRoutines.length > 0) {
          this.fetchMissingRoutines()
        }
      },
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    canCreateRoutines() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    selectOptions() {
      const options = this.$constants.routines.advancedSelectorOptions.map(
        option => ({
          ...option,
          name: this.$t(option?.name),
        })
      )

      return this.canCreateRoutines
        ? options
        : options.filter(option => option.value !== CREATED_BY_ME)
    },

    totalRoutines() {
      return _.chain([
        ...(this.selectedRoutineIds ?? []).map(id => this.cachedRoutines[id]),
        ...this.routines,
      ])
        .uniqBy('id')
        .compact()
        .value()
    },

    selectedRoutines() {
      return this.totalRoutines.filter(routine =>
        (this.selectedRoutineIds ?? []).includes(routine?.id)
      )
    },

    missingRoutines() {
      return (this.selectedRoutineIds ?? []).filter(
        id => !this.cachedRoutines[id]
      )
    },
  },

  methods: {
    handleOpenAdvanceModal() {
      this.isAdvancedRoutineSelectorShown = true
    },

    onRemove(routine) {
      const difference = this.selectedRoutines.filter(
        storedRoutine => storedRoutine.id != routine.id
      )

      this.$emit('select', difference.map(getEntityId))
    },

    onClearAll() {
      this.$emit('select', [])
    },

    handleRoutineSelectChange(option, searchTerm) {
      this.routineFilterOption = option
      this.getRoutines(searchTerm)
    },

    buildRoutinesCache(routinesToAdd) {
      this.cachedRoutines = {
        ...this.cachedRoutines,
        ..._.keyBy(routinesToAdd, 'id'),
      }
    },

    async fetchMissingRoutines() {
      const response = await api.routine.getStreamRoutineDefinition({
        filters: {
          ids: [...this.missingRoutines],
        },
        organizationId: this.organizationId,
      })

      const missingRoutinesResponse = response.data.results.map(
        routineDefinition => ({
          name: routineDefinition?.result?.name,
          id: routineDefinition?.result?.id,
          tags: routineDefinition?.result?.presetTags,
          type: routineDefinition?.result?.type,
          situationType:
            routineDefinition?.result?.presetSituationTypes[0] ?? '',
          highlights: routineDefinition?.highlights,
          createdAt: routineDefinition?.result?.createdAt
            ? utils.dates.formatDate(
                routineDefinition?.result?.createdAt,
                'MMM D, YYYY'
              )
            : '-',
        })
      )

      this.routines = [...this.routines, ...missingRoutinesResponse]
      this.buildRoutinesCache(this.routines)
    },

    async getRoutines(terms) {
      try {
        this.isLoading = true
        this.searchTerms = terms
        const params = {
          filters: {},
          limit: 25,
          terms: terms,
          organizationId: this.organizationId,
          expansions: ['site'],
        }

        if (this.routineFilterOption === IS_EXECUTABLE_BY_ME) {
          params.filters.isExecutableByMe = true
        }
        if (this.routineFilterOption === CREATED_BY_ME) {
          params.filters.createdBy = this.accountId
        }
        if (this.routineFilterOption === COLLABORATOR) {
          params.filters.collaborators = [this.accountId]
        }

        if (terms) {
          params.terms = terms
          params.sort = {
            by: '_score',
            order: 'desc',
          }
        }

        const response = await api.routine.getStreamRoutineDefinition(params)

        this.routines = response.data.results.map(routineDefinition => ({
          name: routineDefinition?.result?.name,
          id: routineDefinition?.result?.id,
          tags: routineDefinition?.result?.presetTags,
          type: routineDefinition?.result?.type,
          situationType:
            routineDefinition?.result?.presetSituationTypes[0] ?? '',
          highlights: routineDefinition?.highlights,
          createdAt: routineDefinition?.result?.createdAt
            ? utils.dates.formatDate(
                routineDefinition?.result?.createdAt,
                'MMM D, YYYY'
              )
            : '-',
        }))

        this.buildRoutinesCache(this.routines)

        if (this.missingRoutines.length) {
          await this.fetchMissingRoutines()
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleSelectSingleRoutine(routine) {
      this.$emit('select', [...this.selectedRoutineIds, getEntityId(routine)])
    },

    handleSelectMultipleRoutines(routines) {
      this.buildRoutinesCache(routines)

      this.routines = _.uniqBy([...routines], 'id')

      this.$emit('select', [...routines.map(getEntityId)])
      this.isAdvancedRoutineSelectorShown = false
    },
  },
}
</script>
<style lang="scss">
.routine-search-bar {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: $color-blue-grey-darken2;
  border-radius: 0px !important;
}

.routine-selector-top-filter .v-input__control .v-input__slot {
  fieldset {
    border: none;
    box-shadow: inset 0px -1px 0px 0px $color-blue-grey-lighten2;
  }
}

.search-popover-skeletons
  .v-skeleton-loader
  .v-skeleton-loader__list-item-avatar-two-line {
  height: 40px;
  margin: 4px 0;
  .v-skeleton-loader__avatar {
    margin-left: 4px;
    margin-right: 8px;
    width: 24px;
    height: 24px;
  }
}
.routine-selector-advanced-button {
  position: sticky;
  bottom: 0;
  border-top: 1px solid $color-blue-grey-lighten2;
  background-color: $color-white;
  height: 40px;
}
</style>
